/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { dedupeBy } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { HeartHandshake, Brain, Sparkles, TrendingDown, TrendingUp, AlertCircle, RefreshCw, PhoneCall, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const QUESTION_KEYS = ['ee1', 'ee2', 'ee3', 'ee4', 'dp1', 'dp2', 'dp3', 'pa1', 'pa2', 'pa3'];

const SCALE_LABELS_KEYS = [
  { value: 0, key: 'scaleNever' },
  { value: 1, key: 'scaleFewYear' },
  { value: 2, key: 'scaleMonthly' },
  { value: 3, key: 'scaleFewMonth' },
  { value: 4, key: 'scaleWeekly' },
  { value: 5, key: 'scaleFewWeek' },
  { value: 6, key: 'scaleDaily' },
];

const RISK_STYLES = {
  low: { color: '#166534', bg: '#DCFCE7', icon: TrendingUp },
  moderate: { color: '#B45309', bg: '#FEF3C7', icon: AlertCircle },
  high: { color: '#B91C1C', bg: '#FEE2E2', icon: TrendingDown },
};

const WellnessPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState('intro'); // intro | quiz | result
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [wellnessResources, setWellnessResources] = useState([]);
  const [protocolResources, setProtocolResources] = useState([]);
  const [supportRequests, setSupportRequests] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [w, p, sr] = await Promise.all([
          api.get('/resources', { params: { type: 'wellness' } }),
          api.get('/resources', { params: { type: 'protocol_it' } }),
          api.get('/initiatives', { params: { type: 'wellness' } }),
        ]);
        setWellnessResources(dedupeBy(w.data));
        setProtocolResources(dedupeBy(p.data));
        // Mai due richieste visivamente identiche (stesso autore + titolo), anche se il server ne restituisse più copie
        setSupportRequests(dedupeBy(dedupeBy(sr.data), (i) => `${i?.author?.full_name || i?.author_id}|${(i?.title || '').trim().toLowerCase()}`));
      } catch (err) {
        console.error('WellnessPage: failed to load resources', err);
      }
    };
    load();
  }, []);

  const startQuiz = () => {
    const init = {};
    QUESTION_KEYS.forEach(k => { init[k] = 0; });
    setAnswers(init);
    setStep('quiz');
    setResult(null);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(answers).map(([key, value]) => ({ key, value })),
        save: true,
      };
      const res = await api.post('/wellness/assessment', payload);
      setResult(res.data);
      setStep('result');
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const RiskCard = ({ label, score, dimension }) => (
    <Card className="p-3 rounded-2xl bg-card">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="font-display text-2xl font-semibold mt-1">{score}<span className="text-sm text-muted-foreground">/100</span></div>
      <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: dimension === 'PA' ? '#166534' : (score > 66 ? '#B91C1C' : score > 33 ? '#B45309' : 'var(--wl-primary)') }} />
      </div>
    </Card>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-2xl flex items-center justify-center bg-primary/10 text-primary">
          <HeartHandshake className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{t('wellness')}</h1>
          <p className="text-sm text-muted-foreground">{t('wellnessSubtitle')}</p>
        </div>
      </div>

      {step === 'intro' && (
        <Card className="p-5 rounded-2xl bg-card">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#DBEAFE', color: '#1E3A8A' }}>
              <Brain className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display font-semibold text-lg">{t('burnoutSelfAssessment')}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t('burnoutAssessmentDesc')}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button onClick={startQuiz} className="gap-2" data-testid="wellness-start-quiz-button">
                  <Sparkles className="h-4 w-4" /> {t('startAssessment')}
                </Button>
              </div>
              <div className="mt-3 text-[11px] text-muted-foreground">{t('assessmentDisclaimer')}</div>
            </div>
          </div>
        </Card>
      )}

      {step === 'quiz' && (
        <Card className="p-5 rounded-2xl bg-card space-y-4">
          <div className="font-display font-semibold text-lg">{t('answerHonestly')}</div>
          {QUESTION_KEYS.map((k, idx) => (
            <div key={k} className="space-y-2">
              <div className="text-sm font-medium">
                {idx + 1}. {t(`q_${k}`)}
              </div>
              <div className="grid grid-cols-7 gap-1" data-testid={`wellness-q-${k}`}>
                {SCALE_LABELS_KEYS.map(({ value, key }) => (
                  <button
                    key={value}
                    onClick={() => setAnswers(a => ({ ...a, [k]: value }))}
                    className={`text-[10px] p-1.5 rounded-lg border ${answers[k] === value ? 'bg-primary/10 border-primary/40 text-primary font-semibold' : 'border-border text-muted-foreground hover:bg-secondary'}`}
                    data-testid={`wellness-q-${k}-v${value}`}
                    title={t(key)}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="text-[11px] text-muted-foreground">
            0 = {t('scaleNever')} · 6 = {t('scaleDaily')}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setStep('intro')}>{t('cancel')}</Button>
            <Button onClick={submit} disabled={submitting} data-testid="wellness-submit-button">
              {submitting ? t('loading') : t('confirm')}
            </Button>
          </div>
        </Card>
      )}

      {step === 'result' && result && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          <Card className="p-5 rounded-2xl bg-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('overallRisk')}</div>
                <div className="flex items-center gap-2 mt-1">
                  {(() => {
                    const RiskIcon = RISK_STYLES[result.risk].icon;
                    return (
                      <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold" style={{ backgroundColor: RISK_STYLES[result.risk].bg, color: RISK_STYLES[result.risk].color }} data-testid="wellness-risk-badge">
                        <RiskIcon className="h-4 w-4" /> {t(`risk_${result.risk}`)}
                      </span>
                    );
                  })()}
                  <span className="text-sm text-muted-foreground">{result.composite}/100</span>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={startQuiz} data-testid="wellness-restart-button">
                <RefreshCw className="h-4 w-4 mr-1" /> {t('retake')}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-4">
              <RiskCard label={t('emotionalExhaustion')} score={result.ee_score} dimension="EE" />
              <RiskCard label={t('depersonalization')} score={result.dp_score} dimension="DP" />
              <RiskCard label={t('personalAccomplishment')} score={result.pa_score} dimension="PA" />
            </div>
            <div className="mt-4 p-3 rounded-xl bg-secondary/60 text-sm">
              {t(`recommendation_${result.risk}`)}
            </div>
          </Card>
        </motion.div>
      )}

      {/* Wellness / Support resources */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-semibold text-lg flex items-center gap-2"><HeartHandshake className="h-5 w-5 text-primary" /> {t('supportResources')}</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/resources')}>{t('viewAll')}</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {wellnessResources.length === 0 && (
            <div className="text-sm text-muted-foreground md:col-span-2 p-3">{t('noResources')}</div>
          )}
          {wellnessResources.map(r => (
            <a key={r.id} href={r.url} target="_blank" rel="noopener noreferrer" className="p-3 rounded-2xl bg-card border border-border hover:bg-secondary/60 transition flex items-center gap-3" data-testid="wellness-resource-card">
              <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><HeartHandshake className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{r.title}</div>
                {r.description && <div className="text-xs text-muted-foreground line-clamp-1">{r.description}</div>}
              </div>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </a>
          ))}
        </div>
      </div>

      {/* Italian Protocols */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-semibold text-lg flex items-center gap-2">🇮🇹 {t('italianProtocols')}</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/resources')}>{t('viewAll')}</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {protocolResources.slice(0, 6).map(r => (
            <a key={r.id} href={r.url} target="_blank" rel="noopener noreferrer" className="p-3 rounded-2xl bg-card border border-border hover:bg-secondary/60 transition flex items-center gap-3" data-testid="protocol-resource-card">
              <div className="h-9 w-9 rounded-xl bg-accent text-accent-foreground flex items-center justify-center text-lg">📖</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{r.title}</div>
                {r.description && <div className="text-xs text-muted-foreground line-clamp-1">{r.description}</div>}
              </div>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </a>
          ))}
        </div>
      </div>

      {/* Support requests (Bacheca wellness type) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-semibold text-lg flex items-center gap-2"><PhoneCall className="h-5 w-5 text-primary" /> {t('supportRequests')}</h2>
          <Button size="sm" onClick={() => navigate('/bacheca')} data-testid="wellness-goto-bacheca">{t('addSupportRequest')}</Button>
        </div>
        <div className="space-y-2">
          {supportRequests.length === 0 && (
            <div className="text-sm text-muted-foreground p-3">{t('noSupportRequests')}</div>
          )}
          {supportRequests.slice(0, 5).map(sr => (
            <div key={sr.id} className="p-3 rounded-2xl bg-card border border-border" data-testid="support-request-card">
              <div className="font-semibold text-sm">{sr.title}</div>
              <div className="text-xs text-muted-foreground">{sr.author?.full_name}</div>
              <div className="text-sm mt-1 line-clamp-3 whitespace-pre-wrap">{sr.description}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WellnessPage;
